import { useEffect, type ReactNode } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { hasAdminAccess, hasSuperAdminAccess } from '@/entities/user'
import { DashboardSidebar } from '@/widgets/dashboard-sidebar'
import { designTokens } from '@/shared/lib/design-tokens'
import bgDashboard from '@/shared/assets/bg_dashboard.png'
import bgMobile from '@/shared/assets/bg_mobile.png'
import { routes } from '@/shared/config'
import { BagIcon, CouponIcon, HomeIcon, MessageSquareIcon, UsersIcon } from '@/shared/ui'
import { useAuth } from '@/features/auth'

const baseSidebarItems = [
  {
    key: 'lk',
    label: 'Главная',
    href: routes.dashboardLk,
    renderIcon: (color: string, size: string | number): ReactNode => (
      <HomeIcon color={color} width={size} height={size} />
    ),
  },
  {
    key: 'subscription',
    label: 'Моя подписка',
    href: routes.dashboardSubscription,
    renderIcon: (color: string, size: string | number): ReactNode => (
      <BagIcon color={color} width={size} height={size} />
    ),
  },
  {
    key: 'support',
    label: 'Поддержка',
    href: 'mailto:support@productsense.io',
    renderIcon: (color: string, size: string | number): ReactNode => (
      <MessageSquareIcon color={color} width={size} height={size} />
    ),
  },
]

export function DashboardPage() {
  const location = useLocation()
  const sidebarIconSize = designTokens.components.account.sidebar.iconSize
  const { user } = useAuth()
  const sidebarItems = [...baseSidebarItems]

  useEffect(() => {
    document.title = 'Личный кабинет | Epic Growth'
  }, [])

  if (hasAdminAccess(user)) {
    sidebarItems.push({
      key: 'admin',
      label: 'Admin',
      href: routes.admin,
      renderIcon: (color: string, size: string | number): ReactNode => (
        <CouponIcon color={color} width={size} height={size} />
      ),
    })
  }

  if (hasSuperAdminAccess(user)) {
    sidebarItems.push({
      key: 'super-admin',
      label: 'Super Admin',
      href: routes.superAdmin,
      renderIcon: (color: string, size: string | number): ReactNode => (
        <UsersIcon color={color} width={size} height={size} />
      ),
    })
  }

  const activeKey =
    sidebarItems.find((item) => item.href && location.pathname.startsWith(item.href))?.key ?? 'lk'

  return (
    <section
      className="relative flex h-screen w-full flex-col overflow-hidden"
      style={{
        backgroundColor: designTokens.colors.background.page,
        backgroundImage: `url(${bgDashboard})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }}
    >
      <div
        className="pointer-events-none absolute inset-0 lg:hidden"
        aria-hidden="true"
        style={{
          backgroundImage: `url(${bgMobile})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
      />
      <div className="relative z-10 flex w-full flex-1 min-h-0">
        <DashboardSidebar
          className="hidden lg:flex"
          items={sidebarItems.map((item) => ({
            ...item,
            icon: ({ color }: { color: string }) => item.renderIcon(color, sidebarIconSize),
          }))}
          activeKey={activeKey}
        />
        <main className="flex w-full flex-1 min-h-0 flex-col items-center overflow-y-auto">
          <div
            className="flex w-full flex-1 flex-col px-4 lg:px-8 lg:pb-8"
            style={{
              gap: designTokens.spacing.xxl,
              maxWidth: designTokens.components.account.layout.contentWidth,
              paddingTop: designTokens.spacing.xxxl,
            }}
          >
            <Outlet />
          </div>
        </main>
      </div>
    </section>
  )
}
